import './style.scss'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { FiFile } from 'react-icons/fi' // FiCode and FiUploadCloud removed as not used now
import utility from 'src/utils/utility'
import dateTime from 'src/utils/dateTime'
import {
    Card, Button, Text, Flex, Box, tokens, InfoBanner, Checkbox, Badge,
    InstitutionTable, type TableColumn,
} from 'src/componentv2'
import { PAGE_PADDING_X, fontMedium } from 'src/model/v2/interface/constants'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'
import type { AndroidBuildRecordStatus } from 'src/model/v2/interface/params'

type PageTab = 'auto-deployment' | 'build-history'

// Kept for future reference – not used in the current UI
// type CredentialsMode = 'paste' | 'upload'

interface SavedCredentials {
    fileName: string
    savedAt: string
    preview: string
}

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
    Success: { bg: '#ECFDF3', color: '#027A48' },
    Queued: { bg: '#FFFAEB', color: '#B54708' },
    InProgress: { bg: '#FFFAEB', color: '#B54708' },
}
const FAILED_STYLE = { bg: '#FEF3F2', color: '#B42318' }
const DEFAULT_STATUS_STYLE = { bg: '#F2F4F7', color: '#344054' }

const statusStyle = (statusMessage: string | null) => {
    if (!statusMessage) return DEFAULT_STATUS_STYLE
    if (statusMessage.startsWith('Failed')) return FAILED_STYLE
    return STATUS_STYLES[statusMessage] ?? DEFAULT_STATUS_STYLE
}

const HISTORY_PAGE_SIZE = 10
const STATUS_POLL_INTERVAL_MS = 4000

const formatNow = () => new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
})

const formatDate = (value: string | null) => {
    const parsed = dateTime.parseUtc(value)
    return parsed ? parsed.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }) : '—'
}

// Kept for future file upload support
// const readFileAsText = (file: File): Promise<string> => new Promise((resolve, reject) => {
//     const reader = new FileReader()
//     reader.onload = () => resolve(String(reader.result ?? ''))
//     reader.onerror = () => reject(reader.error)
//     reader.readAsText(file)
// })

const AutoDeploymentPage = () => {
    const { saveAutoDeploymentCredentials, triggerAutoDeployment, getAutoDeploymentStatus } = useContext(ConfigurationApiContext)

    const [pageTab, setPageTab] = useState<PageTab>('auto-deployment')
    const [pastedJson, setPastedJson] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [saved, setSaved] = useState<SavedCredentials | null>(null)
    const [agreed, setAgreed] = useState(false)
    const [isDeploying, setIsDeploying] = useState(false)
    const [buildStatus, setBuildStatus] = useState<AndroidBuildRecordStatus | null>(null)
    const [historyPage, setHistoryPage] = useState(1)
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const isMountedRef = useRef(true)

    // Removed file-related state and refs – they can be re-added when upload is needed
    // const [mode, setMode] = useState<CredentialsMode>('paste')
    // const [pickedFile, setPickedFile] = useState<File | null>(null)
    // const fileInputRef = useRef<HTMLInputElement>(null)

    const canSave = pastedJson.trim().length > 0

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
        }
    }

    const refreshStatus = async () => {
        const status = await getAutoDeploymentStatus()
        if (!isMountedRef.current) return status
        setBuildStatus(status)
        setIsDeploying(!!status?.hasPendingBuild)
        if (!status?.hasPendingBuild) stopPolling()
        return status
    }

    const startPolling = () => {
        stopPolling()
        pollRef.current = setInterval(refreshStatus, STATUS_POLL_INTERVAL_MS)
    }

    useEffect(() => {
        isMountedRef.current = true
        refreshStatus().then((status) => {
            if (isMountedRef.current && status?.hasPendingBuild) startPolling()
        })
        return () => {
            isMountedRef.current = false
            stopPolling()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSave = async () => {
        if (!canSave || isSaving) return
        setIsSaving(true)
        try {
            const text = pastedJson
            const ok = await saveAutoDeploymentCredentials(text)
            if (ok) {
                setSaved({
                    fileName: 'service-account.json',
                    savedAt: formatNow(),
                    preview: '{ "type": "service_account", "project_id": "••••••••••••", "private_key_id": "••••••••••••"...}',
                })
            }
        } finally {
            setIsSaving(false)
        }
    }

    const handleInitiate = async () => {
        if (!agreed || isDeploying || !saved) return
        const result = await triggerAutoDeployment()
        if (!result) return

        if (result.outcome === 'Queued') {
            setAgreed(false)
            setPageTab('build-history')
            await refreshStatus()
            startPolling()
        }
        // AlreadyPending / InstitutionNotFound: triggerAutoDeployment already showed a notifier message.
    }

    const historyColumns: TableColumn<AndroidBuildRecordStatus>[] = [
        { key: 'buildType', title: 'Build Type', width: '17rem', dataIndex: 'buildType' },
        {
            key: 'statusMessage',
            title: 'Status',
            width: '17rem',
            render: (row) => {
                const s = statusStyle(row.statusMessage)
                return <Badge bgColor={s.bg} color={s.color} radius="99">{row.statusMessage ?? 'Unknown'}</Badge>
            },
        },
        { key: 'currentBuildStartDateTime', title: 'Started', width: '22rem', render: (row) => formatDate(row.currentBuildStartDateTime) },
        { key: 'currentBuildCompletionDateTime', title: 'Completed', width: '22rem', render: (row) => formatDate(row.currentBuildCompletionDateTime) },
        {
            key: 'lastBuildUrl',
            title: 'Build Reference',
            width: '20rem',
            render: (row) => (row.lastBuildUrl ? <a href={row.lastBuildUrl} target="_blank" rel="noreferrer">View</a> : '—'),
        },
    ]

    const historyRows = buildStatus ? [buildStatus] : []

    return (
        <div className="auto-deployment-page" style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
            <Text fontSize="3" style={fontMedium} color={tokens.textDark}>
                Auto deployment
            </Text>

            <div className="auto-deployment-page__tabs">
                <button
                    type="button"
                    className={`auto-deployment-page__tab ${pageTab === 'auto-deployment' ? 'auto-deployment-page__tab--active' : ''}`}
                    onClick={() => setPageTab('auto-deployment')}
                >
                    Auto deployment
                </button>
                <button
                    type="button"
                    className={`auto-deployment-page__tab ${pageTab === 'build-history' ? 'auto-deployment-page__tab--active' : ''}`}
                    onClick={() => setPageTab('build-history')}
                >
                    Build History
                </button>
            </div>

            {pageTab === 'auto-deployment' ? (
                <Box topMargin="2.4">
                    <Text fontSize="1.6" color={tokens.textMuted}>
                        Input and manage your Google account credentials
                    </Text>

                    <Box topMargin="2">
                        <InfoBanner>
                            Credentials are encrypted at rest and can only be uploaded once. To change them
                            later, you&apos;ll need to revoke and re-upload
                        </InfoBanner>
                    </Box>

                    <Box topMargin="2">
                        <Text fontSize="1.4" style={fontMedium} color="#344054">
                            Google service account credentials
                        </Text>
                    </Box>

                    {!saved && (
                        <>
                            {/* No mode pill – only text paste is available */}
                            <Box topMargin="1.6">
                                <textarea
                                    className="auto-deployment-page__textarea"
                                    placeholder="Paste the full JSON string"
                                    value={pastedJson}
                                    onChange={(e) => setPastedJson(e.target.value)}
                                />
                            </Box>

                            <Flex justify="flex-end" topMargin="2">
                                <Button onClick={handleSave} disabled={!canSave || isSaving}>
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                            </Flex>
                        </>
                    )}

                    {saved && (
                        <>
                            <Card padding="0" radius="1.2" topMargin="1.6" className="auto-deployment-page__file-card">
                                <Flex justify="space-between" items="flex-start" padding="2">
                                    <Flex gap="1.2" items="center">
                                        <Box
                                            width="3.2" height="3.2" radius="0.8"
                                            bgColor={tokens.primarySoft}
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <FiFile size={16} color={tokens.primary} />
                                        </Box>
                                        <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>
                                            {saved.fileName}
                                        </Text>
                                    </Flex>
                                    <Flex gap="1.2" items="center">
                                        <Badge bgColor="#ECFDF3" color="#027A48" radius="99">Saved</Badge>
                                        <Text fontSize="1.2" color="#475467">{saved.savedAt}</Text>
                                    </Flex>
                                </Flex>

                                <Box bgColor={tokens.pageBg} borderColor={tokens.border} padding="2">
                                    <Text fontSize="1.4" style={fontMedium} color="#344054">
                                        {saved.preview}
                                    </Text>
                                </Box>

                                <Box padding="2">
                                    <Text fontSize="1.4" style={{ ...fontMedium, display: 'block' }} color={tokens.textDark}>
                                        Ready to deploy
                                    </Text>
                                    <Text fontSize="1.4" color={tokens.textMuted} topMargin="0.2" style={{ display: 'block' }}>
                                        Your credentials are saved. You can trigger auto deployment at any time.
                                    </Text>

                                    <Box topMargin="2">
                                        <Checkbox
                                            checked={agreed}
                                            onChange={setAgreed}
                                            label={(
                                                <Text fontSize="1.4" color="#344054">
                                                    I have read and agreed to the{' '}
                                                    <Text as="span" style={fontMedium} color={tokens.primary}>Legal Disclaimer</Text>
                                                    {' and '}
                                                    <Text as="span" style={fontMedium} color={tokens.primary}>User Agreement.</Text>
                                                </Text>
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </Card>

                            <Flex justify="flex-end" topMargin="2">
                                <Button onClick={handleInitiate} disabled={!agreed || isDeploying}>
                                    {isDeploying ? 'Deploying...' : 'Initiate Auto deployment'}
                                </Button>
                            </Flex>
                        </>
                    )}
                </Box>
            ) : (
                <Box topMargin="2.4">
                    <Text fontSize="1.6" color={tokens.textMuted} className="auto-deployment-page__subtitle">
                        The current or most recent auto deployment build for this institution.
                    </Text>

                    <Card padding="0" topMargin="1.6" radius="1.2">
                        <InstitutionTable
                            columns={historyColumns}
                            data={historyRows}
                            pageSize={HISTORY_PAGE_SIZE}
                            currentPage={historyPage}
                            onPageChange={setHistoryPage}
                            emptyText="No deployments yet."
                        />
                    </Card>
                </Box>
            )}
        </div>
    )
}

export default utility.routeData('auto-deployment', 'Auto Deployment', <AutoDeploymentPage />)